import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, LOGO_DARK, TIMEZONE_OPTIONS, STRIPE_PAYMENT_LINKS } from '../config';
import BottomNavBar from './BottomNavBar';
import AppNotificationsCard from './AppNotificationsCard';
import StravaInterestCard from './StravaInterestCard';
import GoogleHealthCard from './GoogleHealthCard';

const SettingsPage = () => {
  const [user, setUser] = useState(null);
  const [timezone, setTimezone] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [preferredTime, setPreferredTime] = useState('08:00');
  const [secondaryEnabled, setSecondaryEnabled] = useState(false);
  const [secondaryTime, setSecondaryTime] = useState('20:00');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const loadUser = async () => {
    try {
      const res = await axios.get(`${API}/me`);
      setUser(res.data);
      setTimezone(res.data.timezone || '');
      setNotificationsEnabled(res.data.notificationsEnabled || false);
      setPreferredTime(res.data.preferredReminderTime || '08:00');
      setSecondaryEnabled(res.data.secondaryRemindersEnabled || false);
      setSecondaryTime(res.data.secondaryReminderTime || '20:00');
      setLoading(false);
    } catch (err) {
      navigate('/', { replace: true });
    }
  };

  // Check for success/canceled query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      // Reload user data after successful payment
      (async () => { await loadUser(); })();
    }
  }, [location.search]);

  useEffect(() => {
    (async () => { await loadUser(); })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/me`, {
        name: user.name,
        timezone: timezone,
        notificationsEnabled: notificationsEnabled,
        preferredReminderTime: preferredTime,
        secondaryRemindersEnabled: secondaryEnabled,
        secondaryReminderTime: secondaryTime
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Check if user is premium (includes trial, founder, premium)
  const isPremium = user?.isPremium || user?.isFounder || user?.hasActiveSubscription;

  const handleNotificationToggle = () => {
    if (!isPremium) {
      setShowUpgradeModal(true);
      return;
    }
    setNotificationsEnabled(!notificationsEnabled);
  };

  const handleTimeClick = () => {
    if (!isPremium) {
      setShowUpgradeModal(true);
    }
  };

  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      // Use direct Stripe Payment Links to bypass backend API key issues
      const paymentUrl = plan === 'monthly' 
        ? STRIPE_PAYMENT_LINKS.MONTHLY 
        : STRIPE_PAYMENT_LINKS.ANNUAL;
      
      // Add user email as prefilled parameter for better UX
      const urlWithEmail = user?.email 
        ? `${paymentUrl}?prefilled_email=${encodeURIComponent(user.email)}`
        : paymentUrl;
      
      window.location.href = urlWithEmail;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/create-portal-session`);
      window.location.href = res.data.portal_url;
    } catch (err) {
      console.error('Portal error:', err.response?.data || err.message);
      alert('Failed to open subscription portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const [giftLoading, setGiftLoading] = useState(null);

  const handleGiftCheckout = (plan) => {
    setGiftLoading(plan);
    // Use direct Stripe Payment Links - recipient email collected via Stripe custom field
    const paymentUrl = plan === '90day' 
      ? STRIPE_PAYMENT_LINKS.GIFT_90DAY 
      : STRIPE_PAYMENT_LINKS.GIFT_YEAR;
    
    window.location.href = paymentUrl;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      /* ignore logout errors */
    }
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src={LOGO_DARK} alt="Daily Discipline" className="h-8 w-8" />
              <span className="text-lg font-bold text-gray-900">Daily Discipline™</span>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Preferences</h1>
          <p className="text-gray-500">Manage your account settings.</p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">{user?.name?.[0]?.toUpperCase() || '👤'}</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{user?.name || 'User'}</h2>
              <p className="text-gray-500 text-sm">{user?.email}</p>
            </div>
          </div>

          {/* Grandfather Status Badge */}
          {user?.isGrandfathered && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">⭐</span>
                <div>
                  <h3 className="font-semibold text-emerald-800">Founding Member</h3>
                  <p className="text-emerald-600 text-sm">Lifetime access - Thank you for believing early!</p>
                </div>
              </div>
            </div>
          )}

          {/* Timezone Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              My Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
            >
              <option value="">Select your timezone</option>
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <p className="text-gray-500 text-xs mt-2">
              Used for sending reminders at the right time in your local timezone.
            </p>
          </div>

          {/* Daily Accountability Email - Premium Feature */}
          <div className={`mb-4 p-4 rounded-xl border-2 ${isPremium ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">🌅</span>
                <label className="font-medium text-gray-900">Morning Reminder</label>
                {isPremium && notificationsEnabled && (
                  <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">Active</span>
                )}
              </div>
              <button
                onClick={handleNotificationToggle}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  isPremium 
                    ? (notificationsEnabled ? 'bg-emerald-500' : 'bg-gray-300')
                    : 'bg-gray-200 cursor-not-allowed'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  notificationsEnabled && isPremium ? 'translate-x-6' : ''
                }`} />
                {!isPremium && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </button>
            </div>

            {isPremium ? (
              <>
                <p className="text-gray-600 text-sm mb-3">
                  Start your day with a reminder to complete your Tiny Win.
                </p>
                {notificationsEnabled && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Morning Reminder Time
                    </label>
                    <input
                      type="time"
                      value={preferredTime}
                      onChange={(e) => setPreferredTime(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    />
                  </div>
                )}
              </>
            ) : (
              <div onClick={() => setShowUpgradeModal(true)} className="cursor-pointer">
                <p className="text-gray-500 text-sm mb-2">
                  Accountability is key to discipline.
                </p>
                <p className="text-purple-600 text-sm font-medium hover:text-purple-700">
                  🔒 Upgrade to Premium to unlock personalized daily reminders →
                </p>
              </div>
            )}
          </div>

          {/* Secondary Reminder (Evening) - Premium Feature */}
          {isPremium && (
            <div className={`mb-6 p-4 rounded-xl border-2 ${secondaryEnabled ? 'border-purple-200 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🌙</span>
                  <label className="font-medium text-gray-900">Evening Reminder</label>
                  <span className="text-xs text-gray-500">(Optional)</span>
                  {secondaryEnabled && (
                    <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">Active</span>
                  )}
                </div>
                <button
                  onClick={() => setSecondaryEnabled(!secondaryEnabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    secondaryEnabled ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    secondaryEnabled ? 'translate-x-6' : ''
                  }`} />
                </button>
              </div>

              <p className="text-gray-600 text-sm mb-3">
                Great for evening habits or an end-of-day review. Perfect if you have both morning and evening routines.
              </p>

              {secondaryEnabled && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evening Reminder Time
                  </label>
                  <input
                    type="time"
                    value={secondaryTime}
                    onChange={(e) => setSecondaryTime(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white"
                  />
                  <p className="text-gray-500 text-xs mt-2">
                    We&apos;ll send a second reminder at this time for your evening habits.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full py-3 font-semibold rounded-xl transition ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            } disabled:opacity-50`}
          >
            {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
          </button>
        </div>

        {/* App / Phone Notifications + Install (free for all users) */}
        <AppNotificationsCard user={user} />

        {/* Google Health connection (testers now; waitlist for everyone else) */}
        <GoogleHealthCard />

        {/* Strava interest waitlist (gauge demand before building) */}
        <StravaInterestCard />

        {/* Account Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Account</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Email</span>
              <span className="text-gray-900">{user?.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Member Since</span>
              <span className="text-gray-900">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Status</span>
              <span className={`font-medium ${user?.isPremium ? 'text-emerald-600' : 'text-gray-900'}`}>
                {user?.isGrandfathered ? 'Founding Member' : user?.isPremium ? 'Pro Member' : 'Free'}
              </span>
            </div>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h3>
          
          {user?.isPremium ? (
            /* Premium User View */
            <div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">✨</span>
                  <div>
                    <h4 className="font-semibold text-emerald-800">Pro Member</h4>
                    <p className="text-emerald-600 text-sm">
                      {user?.subscriptionPlan === 'annual' ? 'Annual Plan' : 
                       user?.subscriptionPlan === 'monthly' ? 'Monthly Plan' : 
                       user?.isGrandfathered ? 'Lifetime Access' :
                       user?.hasActiveGift ? 'Gift Subscription' : 'Active'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Only show manage button for Stripe subscribers */}
              {user?.stripeCustomerId && !user?.isGrandfathered && (
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition disabled:opacity-50"
                >
                  {portalLoading ? 'Loading...' : 'Manage Subscription'}
                </button>
              )}
            </div>
          ) : (
            /* Free User View - Show upgrade options with clear value */
            <div>
              {/* Pro Benefits */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4">
                <h4 className="font-semibold text-emerald-800 mb-2">✨ Pro unlocks:</h4>
                <ul className="text-sm text-emerald-700 space-y-1">
                  <li>🤖 Unlimited AI Habit Coach (Tiny Wins Architect)</li>
                  <li>🎯 Track up to 3 goals at once</li>
                  <li>🔄 Adaptive check-ins, rescue & graduation</li>
                  <li>🏋️ Fitness scheduler with day-specific reminders</li>
                  <li>💙 Priority support — you fuel indie development</li>
                </ul>
                <p className="text-xs text-emerald-600 mt-3">
                  Phone reminders & habit tracking stay free for everyone.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleCheckout('monthly')}
                  disabled={checkoutLoading}
                  className="py-4 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition disabled:opacity-50"
                >
                  {checkoutLoading === 'monthly' ? 'Loading...' : (
                    <>
                      <div className="text-lg">$7</div>
                      <div className="text-xs opacity-80">per month</div>
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleCheckout('annual')}
                  disabled={checkoutLoading}
                  className="py-4 px-3 bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 font-semibold rounded-xl transition disabled:opacity-50 relative"
                >
                  {checkoutLoading === 'annual' ? 'Loading...' : (
                    <>
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Save 40%</div>
                      <div className="text-lg">$49</div>
                      <div className="text-xs opacity-80">per year</div>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Gift a Subscription */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">🎁 Gift a Subscription</h3>
          <p className="text-gray-600 text-sm mb-4">
            Gift a friend a path to better habits. You&apos;ll enter their email on the next page.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleGiftCheckout('90day')}
              disabled={giftLoading}
              className="bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold py-4 px-4 rounded-xl hover:opacity-90 transition disabled:opacity-50"
            >
              {giftLoading === '90day' ? 'Loading...' : (
                <>
                  <div className="text-sm">90-Day Jumpstart</div>
                  <div className="text-lg font-bold">$19</div>
                </>
              )}
            </button>
            <button
              onClick={() => handleGiftCheckout('year')}
              disabled={giftLoading}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold py-4 px-4 rounded-xl hover:opacity-90 transition disabled:opacity-50 relative"
            >
              {giftLoading === 'year' ? 'Loading...' : (
                <>
                  <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold">Best Value</div>
                  <div className="text-sm">1 Year of Growth</div>
                  <div className="text-lg font-bold">$49</div>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Legal Links */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Legal</h3>
          <div className="space-y-3">
            <a
              href="/legal/terms"
              className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
            >
              <span className="text-gray-700 font-medium">Terms of Use</span>
              <span className="text-gray-400">→</span>
            </a>
            <a
              href="/legal/privacy"
              className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
            >
              <span className="text-gray-700 font-medium">Privacy Policy</span>
              <span className="text-gray-400">→</span>
            </a>
            <a
              href="/legal/refund"
              className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
            >
              <span className="text-gray-700 font-medium">Refund Policy</span>
              <span className="text-gray-400">→</span>
            </a>
          </div>
          {user?.termsAcceptedAt && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              Terms accepted on {new Date(user.termsAcceptedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavBar currentPath="/settings" />

      {/* Upgrade Modal for Notifications */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <span className="text-5xl">🚀</span>
              <h2 className="text-2xl font-bold text-gray-900 mt-4">Unlock Premium</h2>
              <p className="text-gray-600 mt-2">
                Your AI coach, multiple goals, and accountability — all in one.
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <p className="text-purple-800 text-sm">
                <strong>Premium members unlock:</strong>
              </p>
              <ul className="text-purple-700 text-sm mt-2 space-y-1">
                <li>🤖 Unlimited AI Habit Coach</li>
                <li>🎯 Up to 3 goals at once</li>
                <li>🌙 Morning + evening accountability emails</li>
                <li>🔄 Adaptive rescue & graduation</li>
              </ul>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  window.location.href = STRIPE_PAYMENT_LINKS.MONTHLY;
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition"
              >
                Start Free Trial - $7/month
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  window.location.href = STRIPE_PAYMENT_LINKS.ANNUAL;
                }}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-xl transition"
              >
                Best Value - $49/year
              </button>
            </div>

            <button
              onClick={() => setShowUpgradeModal(false)}
              className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
